"use client";

import { merveillesPresidentielles, formatPrice } from "@/lib/data";
import { useCart } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Crown, Gem, ShieldCheck, ScrollText, Landmark, ShoppingBag, BadgeCheck } from "lucide-react";
import Image from "next/image";

const protocolServices = [
  { icon: Crown, title: "Garantie Souveraine 5 Ans", sub: "Remplacement immédiat & technicien d'État à domicile sous 12h" },
  { icon: ScrollText, title: "Accompagnement Protocolaire", sub: "Conseiller dédié formé aux usages diplomatiques et cérémoniels" },
  { icon: ShieldCheck, title: "Installation d'État", sub: "Mise en service sécurisée par nos ingénieurs certifiés souveraineté" },
  { icon: Landmark, title: "Hotline Présidentielle", sub: "Ligne chiffrée dédiée disponible 24h/24, 7j/7, 365 jours" },
];

export function Presidentielle() {
  const addItem = useCart((s) => s.addItem);

  return (
    <section id="presidentiel" className="relative py-16 sm:py-24 overflow-hidden bg-[#0B0804]">
      {/* Fond majestueux */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 35% at 50% 0%, rgba(255,215,0,0.12), transparent), radial-gradient(ellipse 45% 30% at 12% 70%, rgba(0,107,63,0.22), transparent), radial-gradient(ellipse 40% 30% at 88% 85%, rgba(255,176,0,0.08), transparent)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #FFD700 0, #FFD700 1px, transparent 0, transparent 14px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* En-tête */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full border border-[#FFD700]/40 bg-gradient-to-r from-[#1A1408] to-[#2A1F0E] mb-6"
          >
            <Crown className="w-4 h-4 text-[#FFD700]" />
            <span className="text-[#FFD700] text-xs font-black tracking-[0.3em] uppercase">
              Nouveauté Absolue · Collection d&apos;État
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight"
          >
            Les Merveilles{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#FFD700] via-[#FFE87C] to-[#DAA520] bg-clip-text text-transparent">
                Présidentielles
              </span>
              <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
            </span>
          </motion.h2>
          <div className="senegal-bar mx-auto mt-6" />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 text-white/60 leading-relaxed text-base sm:text-lg"
          >
            Huit œuvres d&apos;exception créées pour les chefs d&apos;État, ambassadeurs et
            décideurs souverains. Chaque pièce est <span className="text-[#FFD700]">numérotée</span>,{" "}
            <span className="text-[#FFD700]">certifiée</span> et scellée par nos maîtres
            artisans de Dakar.
          </motion.p>
        </div>

        {/* Produits présidentiels */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {merveillesPresidentielles.map((p, idx) => {
            const discount = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: (idx % 4) * 0.1 }}
                className={`group relative rounded-3xl overflow-hidden border border-[#FFD700]/20 bg-gradient-to-b from-[#171106] to-[#0E0B05] hover:border-[#FFD700]/60 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_-15px_rgba(255,215,0,0.25)] ${
                  idx >= 6 ? "lg:col-span-2" : ""
                }`}
              >
                {/* Image */}
                <div className={`relative overflow-hidden ${idx >= 6 ? "aspect-[21/9]" : "aspect-[4/3]"}`}>
                  <Image
                    src={p.image}
                    alt={p.name}
                    fill
                    className="object-cover group-hover:scale-108 transition-transform duration-700"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E0B05] via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black tracking-widest bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                    <Gem className="w-3 h-3" /> {p.badge}
                  </span>
                  {discount > 0 && (
                    <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#CE1126] text-white text-[11px] font-black shadow-lg">
                      -{discount}%
                    </span>
                  )}
                </div>

                {/* Contenu */}
                <div className="p-5">
                  <p className="text-[10px] font-bold tracking-[0.2em] text-[#C9A227] uppercase">
                    {p.category} · {p.edition}
                  </p>
                  <h3 className="text-lg font-black text-white mt-1.5 leading-snug">
                    {p.name}
                  </h3>
                  <p className="text-xs text-white/50 mt-2 leading-relaxed line-clamp-3">
                    {p.description}
                  </p>

                  {/* Prestiges */}
                  <div className="mt-3.5 flex flex-wrap gap-1.5">
                    {p.prestige.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FFD700]/8 text-[#FFE87C] border border-[#FFD700]/20"
                      >
                        <BadgeCheck className="w-2.5 h-2.5" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Prix */}
                  <div className="mt-4 flex items-end justify-between gap-2 border-t border-[#FFD700]/10 pt-4">
                    <div>
                      <p className="text-xl font-black text-[#FFD700]">
                        {formatPrice(p.price)}{" "}
                        <span className="text-[10px] font-bold text-[#C9A227]">FCFA</span>
                      </p>
                      {p.oldPrice && (
                        <p className="text-[11px] text-white/35 line-through">
                          {formatPrice(p.oldPrice)} FCFA
                        </p>
                      )}
                      <p className="text-[9px] text-white/40 mt-0.5">
                        ou 6x {formatPrice(Math.round(p.price / 6))} sans frais
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() =>
                        addItem({
                          id: p.id,
                          name: p.name,
                          price: p.price,
                          image: p.image,
                          presidential: true,
                        })
                      }
                      className="bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black font-black hover:shadow-[0_0_25px_rgba(255,215,0,0.45)] shrink-0"
                    >
                      <ShoppingBag className="w-3.5 h-3.5 mr-1" />
                      Acquérir
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Services protocolaires */}
        <div className="mt-14">
          <div className="text-center mb-8">
            <p className="text-xs font-black tracking-[0.3em] text-[#C9A227] uppercase mb-2">
              Services Protocolaires Inclus
            </p>
            <h3 className="text-2xl sm:text-3xl font-black text-white">
              Le Traitement{" "}
              <span className="bg-gradient-to-r from-[#FFD700] to-[#DAA520] bg-clip-text text-transparent">
                Souverain
              </span>
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {protocolServices.map((s) => (
              <div
                key={s.title}
                className="p-5 rounded-2xl bg-gradient-to-b from-[#171106]/80 to-[#0E0B05]/80 border border-[#FFD700]/15 hover:border-[#FFD700]/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FFD700]/20 to-[#DAA520]/10 border border-[#FFD700]/25 flex items-center justify-center mb-3">
                  <s.icon className="w-5 h-5 text-[#FFD700]" />
                </div>
                <p className="text-white font-bold text-sm">{s.title}</p>
                <p className="text-white/45 text-xs mt-1.5 leading-relaxed">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
